import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Send, BookOpen, User, Mail, Phone, MessageCircle } from 'lucide-react';
import { sendEmail } from '../services/emailService';

const SubmitChapter = () => {
  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    paperTitle: '',
    whatsapp: '',
    mobile: '',
    email: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.designation.trim()) newErrors.designation = 'Designation is required';
    if (!formData.paperTitle.trim()) newErrors.paperTitle = 'Paper title is required';
    if (!formData.whatsapp.trim()) {
      newErrors.whatsapp = 'WhatsApp number is required';
    } else if (!/^\d{10}$/.test(formData.whatsapp.replace(/\D/g, ''))) {
      newErrors.whatsapp = 'WhatsApp number must be 10 digits';
    }
    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobile.replace(/\D/g, ''))) {
      newErrors.mobile = 'Mobile number must be 10 digits';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSubmitStatus('');
    
    try {
      const emailData = {
        subject: 'New Chapter Submission',
        html: `
          <h2>New Chapter Submission</h2>
          <p><strong>Name:</strong> ${formData.name}</p>
          <p><strong>Designation:</strong> ${formData.designation}</p>
          <p><strong>Paper Title:</strong> ${formData.paperTitle}</p>
          <p><strong>WhatsApp:</strong> ${formData.whatsapp}</p>
          <p><strong>Mobile:</strong> ${formData.mobile}</p>
          <p><strong>Email:</strong> ${formData.email}</p>
          <p><strong>Submitted on:</strong> ${new Date().toLocaleString()}</p>
        `,
        from_name: formData.name,
        reply_to: formData.email
      };
      
      await sendEmail(emailData);
      setSubmitStatus('success');
      setFormData({ name: '', designation: '', paperTitle: '', whatsapp: '', mobile: '', email: '' });
      setTimeout(() => setSubmitStatus(''), 5000);
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh', paddingTop: '80px' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px' }}>
        <Link 
          to="/books" 
          style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: '8px', 
            color: '#667eea', 
            textDecoration: 'none', 
            marginBottom: '30px', 
            fontSize: '1rem', 
            fontWeight: 600,
            transition: 'color 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.color = '#5a67d8'}
          onMouseLeave={(e) => e.target.style.color = '#667eea'}
        >
          <ArrowLeft size={20} />
          Back to Books
        </Link>

        <div style={{
          background: 'linear-gradient(135deg, #667eea20 0%, #764ba210 100%)',
          padding: '3px',
          borderRadius: '20px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '17px',
            padding: '50px'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 25px',
                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
              }}>
                <BookOpen size={32} color="white" />
              </div>
              <h1 style={{ color: '#2d3748', fontSize: '2.5rem', fontWeight: 700, marginBottom: '15px' }}>
                Submit Book Chapter
              </h1>
              <p style={{ color: '#718096', fontSize: '1.2rem', maxWidth: '600px', margin: '0 auto' }}>
                Submit your research paper or book chapter for our upcoming publications. Join our community of distinguished authors.
              </p>
            </div>

            {submitStatus === 'success' && (
              <div style={{
                backgroundColor: '#c6f6d5',
                color: '#22543d',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '25px',
                textAlign: 'center',
                fontWeight: 600
              }}>
                Chapter submitted successfully! We'll review your submission and contact you soon.
              </div>
            )}

            {submitStatus === 'error' && (
              <div style={{
                backgroundColor: '#fed7d7',
                color: '#c53030',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '25px',
                textAlign: 'center',
                fontWeight: 600
              }}>
                Submission failed. Please try again.
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gap: '25px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div>
                    <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <User size={16} />
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: errors.name ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = errors.name ? '#e53e3e' : '#e2e8f0'}
                    />
                    {errors.name && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.name}</span>}
                  </div>

                  <div>
                    <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>Designation *</label>
                    <input
                      type="text"
                      name="designation"
                      value={formData.designation}
                      onChange={handleChange}
                      placeholder="e.g., Assistant Professor, Research Scholar"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: errors.designation ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = errors.designation ? '#e53e3e' : '#e2e8f0'}
                    />
                    {errors.designation && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.designation}</span>}
                  </div>
                </div>

                <div>
                  <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>Paper Title *</label>
                  <input
                    type="text"
                    name="paperTitle"
                    value={formData.paperTitle}
                    onChange={handleChange}
                    placeholder="Enter your research paper or chapter title"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: errors.paperTitle ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = errors.paperTitle ? '#e53e3e' : '#e2e8f0'}
                  />
                  {errors.paperTitle && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.paperTitle}</span>}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div>
                    <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <MessageCircle size={16} />
                      WhatsApp Number *
                    </label>
                    <input
                      type="tel"
                      name="whatsapp"
                      value={formData.whatsapp}
                      onChange={handleChange}
                      placeholder="10-digit number"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: errors.whatsapp ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = errors.whatsapp ? '#e53e3e' : '#e2e8f0'}
                    />
                    {errors.whatsapp && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.whatsapp}</span>}
                  </div>

                  <div>
                    <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Phone size={16} />
                      Mobile Number *
                    </label>
                    <input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      placeholder="10-digit number"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: errors.mobile ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'border-color 0.2s'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#667eea'}
                      onBlur={(e) => e.target.style.borderColor = errors.mobile ? '#e53e3e' : '#e2e8f0'}
                    />
                    {errors.mobile && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.mobile}</span>}
                  </div>
                </div>

                <div>
                  <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Mail size={16} />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your.email@example.com"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: errors.email ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                    onBlur={(e) => e.target.style.borderColor = errors.email ? '#e53e3e' : '#e2e8f0'}
                  />
                  {errors.email && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.email}</span>}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    width: '100%',
                    padding: '16px',
                    backgroundColor: isSubmitting ? '#a0aec0' : '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    transition: 'all 0.2s ease',
                    marginTop: '10px'
                  }}
                >
                  <Send size={20} />
                  {isSubmitting ? 'Submitting...' : 'Submit Chapter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmitChapter;